import React, { useState } from "react";
import {
  Typography,
  Menu,
  MenuItem,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import FileOpenOutlinedIcon from "@mui/icons-material/FileOpenOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import ComputerOutlinedIcon from "@mui/icons-material/ComputerOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import AddToDriveOutlinedIcon from "@mui/icons-material/AddToDriveOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import SaveToBrowserDialog from "./SaveToBrowserDialog";
import OpenFromBrowserDialog from "./OpenFromBrowserDialog";
import NewDesignConfirmationDialog from "./NewDesignConfirmationDialog";
import SaveToComputerDialog from "./SaveToComputerDialog";
import SaveToMyAccountDialog from "./SaveToMyAccountDialog";
import OpenFromMyAccountDialog from "./OpenFromMyAccountDialog"; // Import the new dialog
import RenameProjectDialog from "./RenameProjectDialog";
import { useData } from "../hooks/useData";

const FileMenu = ({ canvas }) => {
  const { setProjectName, setBackgroundColor, saveHistory, projectName } = useData();
  const [anchorEl, setAnchorEl] = useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [openDialogOpen, setOpenDialogOpen] = useState(false);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [actionType, setActionType] = useState("new");
  const [saveToComputerDialogOpen, setSaveToComputerDialogOpen] = useState(false);
  const [saveToMyAccountDialogOpen, setSaveToMyAccountDialogOpen] = useState(false);
  const [openMyAccountDialogOpen, setOpenMyAccountDialogOpen] = useState(false); // State for the new dialog
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [activeSaveDialog, setActiveSaveDialog] = useState(null);

  const open = Boolean(anchorEl);

  const isDisabled = !canvas || canvas.getObjects().length === 0;
  const isRenameDisabled = !projectName;

  const handleClick = (event) => {
    console.log("[FileMenu]: handleClick");
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    console.log("[FileMenu]: handleClose");
    setAnchorEl(null);
  };

  const handleNew = () => {
    console.log("[FileMenu]: handleNew");
    setActionType("new");
    if (canvas && canvas.getObjects().length > 0) {
      setNewDialogOpen(true);
    } else {
      handleNewDesign();
    }
    setAnchorEl(null);
  };

  const handleNewDesign = () => {
    if (!canvas) return;
    try {
      canvas.clear();
      canvas.backgroundColor = "#ffffff";
      setBackgroundColor("#ffffff");
      canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
      canvas.setZoom(1);
      canvas.renderAll();
      canvas.requestRenderAll();
      setProjectName("");
      saveHistory(canvas);
      setSnackbarMessage(
        actionType === "new"
          ? "New design created successfully!"
          : "Design moved to trash successfully!"
      );
      setSnackbarOpen(true);
    } catch (e) {
      console.error("Error creating new design:", e);
      setSnackbarMessage("Failed to create new design.");
      setSnackbarOpen(true);
    }
  };

  const handleOpen = () => {
    console.log("[FileMenu]: handleOpen");
    setOpenMyAccountDialogOpen(true); // Open the new dialog
    setAnchorEl(null);
  };

  const handleOpenFromBrowser = () => {
    console.log("[FileMenu]: handleOpenFromBrowser");
    setOpenDialogOpen(true);
    setAnchorEl(null);
  };

  const handleRename = () => {
    console.log("[FileMenu]: handleRename");
    setRenameDialogOpen(true);
    setAnchorEl(null);
  };

  const handleSaveToBrowser = () => {
    console.log("[FileMenu]: handleSaveToBrowser");
    setSaveDialogOpen(true);
    setActiveSaveDialog("browser");
    setAnchorEl(null);
  };

  const handleSaveMyComputer = () => {
    console.log("[FileMenu]: handleSaveMyComputer");
    setSaveToComputerDialogOpen(true);
    setActiveSaveDialog("computer");
    setAnchorEl(null);
  };

  const handleSaveMyAccount = () => {
    console.log("[FileMenu]: handleSaveMyAccount");
    setSaveToMyAccountDialogOpen(true);
    setActiveSaveDialog("account");
    setAnchorEl(null);
  };

  const handleGoogleDrive = () => {
    console.log("[FileMenu]: handleGoogleDrive");
    setAnchorEl(null);
  };

  const handleTrash = () => {
    console.log("[FileMenu]: handleTrash");
    setActionType("trash");
    if (canvas && canvas.getObjects().length > 0) {
      setNewDialogOpen(true);
    } else {
      handleNewDesign();
    }
  };

  const handleCloseFile = () => {
    console.log("[FileMenu]: handleCloseFile");
    setAnchorEl(null);
  };

  const handleSaveSuccess = () => {
    if (activeSaveDialog === "browser") {
      setSaveDialogOpen(false);
    } else if (activeSaveDialog === "computer") {
      setSaveToComputerDialogOpen(false);
    } else if (activeSaveDialog === "account") {
      setSaveToMyAccountDialogOpen(false);
    }
    setActiveSaveDialog(null);
    setSnackbarMessage("Design saved successfully!");
    setSnackbarOpen(true);
  };

  const handleOpenSuccess = () => {
    setOpenDialogOpen(false);
    setOpenMyAccountDialogOpen(false); // Close both dialogs
    setSnackbarMessage("Design opened successfully!");
    setSnackbarOpen(true);
  };

  const handleRenameSuccess = () => {
    setRenameDialogOpen(false);
    setSnackbarMessage("Project renamed successfully!");
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
    setSnackbarMessage("");
  };

  return (
    <>
      <Typography
        variant="body1"
        onClick={handleClick}
        sx={{
          fontFamily: "Roboto",
          fontWeight: 500,
          color: "#000000",
          cursor: "pointer",
          "&:hover": {
            textDecoration: "underline",
            color: "#000000",
          },
        }}
      >
        File
      </Typography>
      <Menu
        id="file-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableScrollLock
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        MenuListProps={{
          "aria-labelledby": "file-button",
        }}
        sx={{
          "& .MuiPaper-root": {
            minWidth: 200,
            maxWidth: 240,
            boxShadow: 3,
          },
        }}
      >
        <MenuItem onClick={handleNew}>
          <NoteAddOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
          New
        </MenuItem>
        <MenuItem onClick={handleOpen}>
          <FileOpenOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
          Open from My Account
        </MenuItem>
        <MenuItem onClick={handleOpenFromBrowser}>
          <ContentCopyOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
          Open from Browser
        </MenuItem>
        <Divider />
        <MenuItem disabled={isRenameDisabled} onClick={handleRename}>
          <DriveFileRenameOutlineOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
          Rename
        </MenuItem>
        <Divider />
        <MenuItem disabled={isDisabled} onClick={handleSaveToBrowser}>
          <ContentCopyOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
          Save to Browser
        </MenuItem>
        <MenuItem disabled={isDisabled} onClick={handleSaveMyComputer}>
          <ComputerOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
          Save to My Computer
        </MenuItem>
        <MenuItem disabled={isDisabled} onClick={handleSaveMyAccount}>
          <ManageAccountsOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
          Save to My Account
        </MenuItem>
        <MenuItem disabled onClick={handleGoogleDrive}>
          <AddToDriveOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
          Save to Google Drive
        </MenuItem>
        <Divider />
        <MenuItem disabled={isDisabled} onClick={handleTrash}>
          <DeleteOutlineOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
          Move to trash
        </MenuItem>
        <MenuItem onClick={handleCloseFile}>
          <CloseOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
          Close
        </MenuItem>
      </Menu>
      <SaveToBrowserDialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        canvas={canvas}
        onSaveSuccess={handleSaveSuccess}
      />
      <OpenFromBrowserDialog
        open={openDialogOpen}
        onClose={() => setOpenDialogOpen(false)}
        canvas={canvas}
        onOpenSuccess={handleOpenSuccess}
      />
      <NewDesignConfirmationDialog
        open={newDialogOpen}
        onClose={() => setNewDialogOpen(false)}
        onConfirm={() => {
          handleNewDesign();
          setNewDialogOpen(false);
        }}
        action={actionType}
      />
      <SaveToComputerDialog
        open={saveToComputerDialogOpen}
        onClose={() => setSaveToComputerDialogOpen(false)}
        canvas={canvas}
        onSaveSuccess={handleSaveSuccess}
      />
      <SaveToMyAccountDialog
        open={saveToMyAccountDialogOpen}
        onClose={() => setSaveToMyAccountDialogOpen(false)}
        canvas={canvas}
        onSaveSuccess={handleSaveSuccess}
      />
      <OpenFromMyAccountDialog
        open={openMyAccountDialogOpen}
        onClose={() => setOpenMyAccountDialogOpen(false)}
        canvas={canvas}
        onOpenSuccess={handleOpenSuccess}
      />
      <RenameProjectDialog
        open={renameDialogOpen}
        onClose={() => setRenameDialogOpen(false)}
        onRenameSuccess={handleRenameSuccess}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default FileMenu;